import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Button, Divider, Flex, MultiSelect, Paper, SegmentedControl, Select, Stack, TagsInput, Text, TextInput, Title } from "@mantine/core";
import { AppModal } from "../../shared/AppModal";
import { useForm, yupResolver } from "@mantine/form";
import { DeleteOutlineOutlined, FilterAltOutlined, UnfoldMoreOutlined } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { DAYS, TIMES } from "../../datasets/days_available";
import { STATE_OPTIONS } from "../../datasets/states";
import { PrioritySearchSortKey, PrioritySearchSortOrder, useCreatePriorityViewMutation, useGetTherapySubCategoryGroupsQuery, usePriorityEntryModalQuery, useUpdatePriorityViewMutation } from "../../graphql/graphql";
import { useDeviceType } from "../../hooks/useDeviceType";
import { handleNumberInputChange, handleNumericOnlyInputChange } from "../../utils/Form.utils";
import { PriorityViewPreviewEmptyState } from "./PriorityViewPreviewEmptyState";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { PriorityViewTable } from "./PriorityView";
import { AGE_YEARS_FILTER_TYPES, BOOLEAN_FILTER_TYPES, convertFormToPriorityViewCreate, convertPriorityViewToFormValues, DATE_INTERVAL_FILTER_TYPES, getDefaultValuesForInputType, MATCH_TYPES, TIME_WAITING_FILTER_TYPES } from "./EditPriorityViewModal.utils";
import { buildAvailablePrioritySearchFilters, createEditTableViewSchema, EditTableViewInitialValues } from "./EditPriorityViewModal.validation";
const DEFAULT_PREVIEW_FILTERS = {
    pagination: {
        offset: 0,
        limit: 20
    },
    where: {},
    sort: []
};
const THERAPY_LABELS = {
    therapy_category_ids: "Modalities",
    therapy_sub_category_ids: "Skills"
};
// Extracted renderer so we can reuse inside DragOverlay
const renderInputFieldForFilter = (filter, form, graphqlData) => {
    const { id, name } = filter;
    const filterId = `fields.${id}`;
    switch (id) {
        case "age_years":
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: "Select age filter", data: AGE_YEARS_FILTER_TYPES, size: "xs", ...form.getInputProps(`${filterId}.filterType`) }), form.values.fields[id]?.filterType === "between" && (_jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { flex: 1, placeholder: "Min", type: "number", size: "xs", ...form.getInputProps(`${filterId}.between_min`), value: form.values.fields[id]?.between_min ?? "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.between_min`) }), _jsx(TextInput, { flex: 1, placeholder: "Max", type: "number", size: "xs", ...form.getInputProps(`${filterId}.between_max`), value: form.values.fields[id]?.between_max ?? "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.between_max`) })] })), form.values.fields[filter.id]?.filterType ===
                        "older_than" && (_jsx(TextInput, { placeholder: "Age", type: "number", size: "xs", ...form.getInputProps(`${filterId}.older_than`), value: form.values.fields[id]?.older_than ?? "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.older_than`) })), form.values.fields[filter.id]?.filterType ===
                        "younger_than" && (_jsx(TextInput, { placeholder: "Age", type: "number", size: "xs", ...form.getInputProps(`${filterId}.younger_than`), value: form.values.fields[id]?.younger_than ?? "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.younger_than`) }))] }, id));
        case "search":
            return (_jsx(TextInput, { placeholder: `Enter ${name.toLowerCase()}`, size: "xs", ...form.getInputProps(`${filterId}`) }, id));
        case "needs_follow_up":
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: "Select follow-up state", data: BOOLEAN_FILTER_TYPES, size: "xs", value: String(form.values.fields[id]?.value ?? true), onChange: (value) => form.setFieldValue(`${filterId}.value`, value === "true") }), _jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { flex: 1, placeholder: "Number", type: "number", size: "xs", value: form.values.fields[id]?.amount ?? "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.amount`) }), _jsx(Select, { placeholder: "Select interval", data: DATE_INTERVAL_FILTER_TYPES, size: "xs", style: { flex: 1 }, ...form.getInputProps(`${filterId}.interval`) })] })] }, id));
        case "without_assigned_provider":
            return (_jsx(Select, { placeholder: `Select ${name.toLowerCase()}`, data: BOOLEAN_FILTER_TYPES, size: "xs", value: String(form.values.fields[id] ?? true), onChange: (value) => form.setFieldValue(`${filterId}`, value === "true") }, id));
        case "priority_status_id": {
            const options = [
                // Adding the 'not set' option so we can filter for entries that have a null status.
                // This is transformed into 'null' in the utils functions.
                {
                    value: "NOT_SET",
                    label: "No Referral Stage"
                },
                ...(graphqlData.priorityStatuses?.map((status) => ({
                    value: status.priorityStatusId,
                    label: status.name
                })) || [])
            ];
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: `Select match type`, data: [MATCH_TYPES[1], MATCH_TYPES[2]], size: "xs", ...form.getInputProps(`${filterId}.match`) }), _jsx(MultiSelect, { variant: "pill", placeholder: `Select ${name.toLowerCase()}`, data: options ?? [], searchable: true, size: "xs", disabled: !options || options.length === 0, renderOption: (option) => {
                            const statusColor = graphqlData.priorityStatuses?.find((status) => status?.priorityStatusId ===
                                option.option.value)?.colorHex;
                            return (_jsx("div", { style: { width: "100%", height: "100%" }, children: _jsx(Text, { variant: "body", size: "xs", style: {
                                        backgroundColor: statusColor ??
                                            "var(--mantine-color-gray-4)",
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                        paddingLeft: 10,
                                        borderRadius: 4
                                    }, children: option.option.label }) }));
                        }, ...form.getInputProps(`${filterId}.ids`) })] }, id));
        }
        case "condition_ids":
        case "insurance_ids":
        case "therapy_category_ids":
        case "therapy_sub_category_ids":
        case "priority_status_id": {
            const dynamicOptions = getDynamicOptions(id, graphqlData);
            const label = THERAPY_LABELS[id]
                ? THERAPY_LABELS[id]?.toLowerCase()
                : name.toLowerCase();
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: `Select match type`, data: MATCH_TYPES, size: "xs", ...form.getInputProps(`${filterId}.match`) }), _jsx(MultiSelect, { variant: "pill", placeholder: `Select ${label}`, data: dynamicOptions ?? [], searchable: true, size: "xs", disabled: !dynamicOptions || dynamicOptions.length === 0, ...form.getInputProps(`${filterId}.ids`) })] }, id));
        }
        case "days_available": {
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: `Select match type`, data: MATCH_TYPES, size: "xs", ...form.getInputProps(`${filterId}.match`) }), _jsx(MultiSelect, { variant: "pill", placeholder: `Select ${name.toLowerCase()}`, data: DAYS.map((val) => ({
                            value: val.toLowerCase(),
                            label: val
                        })), searchable: true, size: "xs", ...form.getInputProps(`${filterId}.names`) })] }, id));
        }
        case "times_available": {
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: `Select match type`, data: MATCH_TYPES, size: "xs", ...form.getInputProps(`${filterId}.match`) }), _jsx(MultiSelect, { variant: "pill", placeholder: `Select ${name.toLowerCase()}`, data: TIMES.map((val) => ({
                            value: val,
                            label: val
                        })), searchable: true, size: "xs", ...form.getInputProps(`${filterId}.names`) })] }, id));
        }
        case "tags": {
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: `Select match type`, data: MATCH_TYPES, size: "xs", ...form.getInputProps(`${filterId}.match`) }), _jsx(TagsInput, { variant: "pill", placeholder: `Add ${name.toLowerCase()}`, clearable: true, acceptValueOnBlur: true, splitChars: [","], size: "xs", ...form.getInputProps(`${filterId}.names`) })] }, id));
        }
        case "location":
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(SegmentedControl, { size: "xs", value: form.values.fields[id]?.mode ?? "zip", data: [
                            { label: "Zip codes", value: "zip" },
                            { label: "States", value: "states" }
                        ], onChange: (value) => {
                            form.setFieldValue(`${filterId}.mode`, value);
                            if (value === "zip") {
                                // Clear state filter values when switching to zip mode
                                form.setFieldValue(`${filterId}.state_ids`, []);
                            }
                            else {
                                // Clear zip filter values when switching to states mode
                                form.setFieldValue(`${filterId}.distance_in_miles`, null);
                                form.setFieldValue(`${filterId}.postal_codes`, []);
                            }
                        } }), form.values.fields[id]?.mode === "states" ? (_jsx(MultiSelect, { variant: "pill", placeholder: "Select states", data: STATE_OPTIONS, searchable: true, size: "xs", ...form.getInputProps(`${filterId}.state_ids`) })) : (_jsxs(_Fragment, { children: [_jsx(TextInput, { placeholder: "Distance in miles", type: "number", size: "xs", ...form.getInputProps(`${filterId}.distance_in_miles`), value: form.values.fields[id]?.distance_in_miles ??
                                    "", onChange: (e) => handleNumberInputChange(e.currentTarget.value, form, `${filterId}.distance_in_miles`) }), _jsx(TagsInput, { variant: "pill", placeholder: "Zip codes", clearable: true, acceptValueOnBlur: true, splitChars: [","], size: "xs", ...form.getInputProps(`${filterId}.postal_codes`) })] }))] }, id));
        case "time_waiting_date":
            return (_jsxs(Stack, { gap: "xs", children: [_jsx(Select, { placeholder: "Select filter type", data: TIME_WAITING_FILTER_TYPES, size: "xs", ...form.getInputProps(`${filterId}.filterType`) }), form.values.fields[id]?.filterType === "between" && (_jsxs(_Fragment, { children: [_jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { flex: 1, placeholder: "Enter minimum", type: "text", size: "xs", ...form.getInputProps(`${filterId}.between_start`), value: form.values.fields[id]?.between_start ??
                                            "", onChange: (e) => handleNumericOnlyInputChange(e.currentTarget.value, form, `${filterId}.between_start`) }), _jsx(TextInput, { flex: 1, placeholder: "Enter maximum", type: "text", size: "xs", ...form.getInputProps(`${filterId}.between_end`), value: form.values.fields[id]?.between_end ??
                                            "", onChange: (e) => handleNumericOnlyInputChange(e.currentTarget.value, form, `${filterId}.between_end`) })] }), _jsx(Select, { placeholder: "Select interval", data: DATE_INTERVAL_FILTER_TYPES, size: "xs", ...form.getInputProps(`${filterId}.between_interval`) })] })), form.values.fields[id]?.filterType === "longer_than" && (_jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { placeholder: "Enter a number", type: "text", size: "xs", ...form.getInputProps(`${filterId}.longer_than`), value: form.values.fields[id]?.longer_than ?? "", onChange: (e) => handleNumericOnlyInputChange(e.currentTarget.value, form, `${filterId}.longer_than`) }), _jsx(Select, { placeholder: "Select interval", data: DATE_INTERVAL_FILTER_TYPES, size: "xs", ...form.getInputProps(`${filterId}.longer_than_interval`) })] })), form.values.fields[id]?.filterType === "shorter_than" && (_jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { placeholder: "Enter a number", type: "text", size: "xs", ...form.getInputProps(`${filterId}.shorter_than`), value: form.values.fields[id]?.shorter_than ?? "", onChange: (e) => handleNumericOnlyInputChange(e.currentTarget.value, form, `${filterId}.shorter_than`) }), _jsx(Select, { placeholder: "Select interval", data: DATE_INTERVAL_FILTER_TYPES, size: "xs", ...form.getInputProps(`${filterId}.shorter_than_interval`) })] }))] }, id));
        default:
            return null;
    }
};
// Helper function to get dynamic options based on filter type and available data
const getDynamicOptions = (filterId, graphqlData) => {
    switch (filterId) {
        case "condition_ids":
            return graphqlData.conditions;
        case "insurance_ids":
            return graphqlData.insurances;
        case "therapy_category_ids":
            return graphqlData.therapyCategories;
        case "therapy_sub_category_ids":
            return graphqlData.therapySubCategories;
        case "priority_status_id":
            return graphqlData.priorityStatuses;
        default:
            return [];
    }
};
// Filter configuration component
const FilterConfiguration = ({ filter, form, graphqlData, removeFilter, disabled, style }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `enabled-${filter.id}`,
        data: { source: "enabled", id: filter.id },
        disabled: disabled
    });
    const renderInputField = (filter) => renderInputFieldForFilter(filter, form, graphqlData);
    const hasFieldError = form.errors[`fields.${filter.id}`];
    return (_jsx(Paper, { ref: setNodeRef, style: {
            cursor: "grab",
            opacity: isDragging ? 0.5 : 1,
            ...style
        }, id: "drag-indicator", bg: "transparent", ...attributes, ...listeners, children: _jsxs(Stack, { align: "center", gap: "xs", children: [_jsx(Button, { variant: "light", rightSection: _jsx(DeleteOutlineOutlined, { style: { fontSize: 15 } }), justify: "space-between", onClick: (e) => {
                        e.stopPropagation();
                        removeFilter(filter.id);
                    }, style: {
                        backgroundColor: "var(--mantine-color-primary-3)",
                        color: "var(--mantine-color-black)",
                        cursor: "grab"
                    }, size: "xs", px: "xs", w: "100%", children: _jsx(Text, { variant: "body", size: "xs", children: filter.name }) }, filter.id), _jsxs(Stack, { gap: "xs", w: "100%", children: [renderInputField(filter), hasFieldError && (_jsx(Text, { variant: "body", c: "var(--mantine-color-red-5)", size: "xs", children: form.errors[`fields.${filter.id}`] }))] }), !disabled && (_jsx("div", { style: {
                        width: "100%",
                        height: 1,
                        backgroundColor: "var(--mantine-color-gray-3)"
                    } }))] }) }));
};
const AvailableFilterItem = ({ filter, enabled, onToggle, disabled, style }) => {
    const id = filter.id;
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `available-${id}`,
        data: { source: "available", id: id },
        disabled: disabled || enabled
    });
    return (_jsx(Button, { ref: setNodeRef, variant: "light", disabled: disabled || enabled, onClick: () => onToggle(id), justify: "space-between", style: {
            backgroundColor: !enabled
                ? "var(--mantine-color-primary-3)"
                : "var(--mantine-color-gray-1)",
            color: !enabled ? "black" : "var(--mantine-color-gray-5)",
            cursor: "grab",
            opacity: isDragging ? 0.5 : 1,
            ...style
        }, px: "xs", size: "xs", ...attributes, ...listeners, children: _jsx(Text, { variant: "body", size: "xs", children: filter.name }) }, id));
};
const DraggableColumn = ({ children, id, ...rest }) => {
    const { isOver, setNodeRef: setNodeRef } = useDroppable({
        id: id
    });
    return (_jsx(Stack, { ...rest, ref: setNodeRef, style: isOver
            ? {
                border: "2px dashed var(--mantine-color-primary-9)",
                borderRadius: 6,
                padding: 8,
                transition: "border 80ms ease-out",
                backgroundColor: "color-mix(in srgb, var(--mantine-color-primary-9) 6%, transparent)"
            }
            : {}, children: children }));
};
export const EditTableViewModal = ({ opened, close, priorityStatusList, refetchPriorityViews, defaultView }) => {
    // Local state for filters
    const [filters, setFilters] = useState([]);
    const [enabledFilters, setEnabledFilters] = useState(new Set());
    const { isAtLeastMediumScreen } = useDeviceType();
    const [{ fetching: createPriorityViewFetching }, createPriorityView] = useCreatePriorityViewMutation();
    const [{ fetching: updatePriorityViewFetching }, updatePriorityView] = useUpdatePriorityViewMutation();
    const availableFilters = useMemo(() => buildAvailablePrioritySearchFilters(), []);
    const form = useForm({
        mode: "controlled",
        initialValues: EditTableViewInitialValues,
        validate: yupResolver(createEditTableViewSchema(Array.from(enabledFilters)))
    });
    const [{ data: priorityEntryDrawerData }] = usePriorityEntryModalQuery({
        variables: {
            inOrganization: true
        },
        pause: !opened
    });
    // Get only the therapy sub category groups for the selected therapy category
    const [{ data: therapySubCategoriesData, fetching: therapySubCategoriesFetching }] = useGetTherapySubCategoryGroupsQuery({
        variables: {
            therapyCategoryIds: form.values.fields.therapy_category_ids?.ids ?? []
        },
        pause: !form.values.fields.therapy_category_ids?.ids?.length || !opened
    });
    const therapySubCategories = therapySubCategoriesData?.therapySubCategories
        ?.therapySubCategoryGroups ?? [];
    const conditionsData = priorityEntryDrawerData?.conditions?.conditionsList ?? [];
    const insurancesData = priorityEntryDrawerData?.insurances?.insurancesList ?? [];
    const therapyCategoriesData = priorityEntryDrawerData?.therapyCategories?.therapyCategoriesList ?? [];
    const priorityStatusesData = priorityEntryDrawerData?.priorityStatuses?.priorityStatuses ?? [];
    const [previewFilters, setPreviewFilters] = useState(null);
    // Build available filters dynamically from GraphQL data
    const graphqlData = useMemo(() => ({
        conditions: conditionsData,
        insurances: insurancesData,
        therapyCategories: therapyCategoriesData,
        priorityStatuses: priorityStatusesData,
        therapySubCategories: therapySubCategories
    }), [
        conditionsData,
        insurancesData,
        therapyCategoriesData,
        priorityStatusesData,
        therapySubCategoriesData
    ]);
    // Clear validation errors from disabled fields when filters change
    useEffect(() => {
        // Get all field errors
        const fieldErrors = form.errors;
        // Clear errors for fields that are no longer enabled
        Object.keys(fieldErrors).forEach((errorPath) => {
            if (errorPath.startsWith("fields.")) {
                const fieldId = errorPath.split(".")[1]; // Extract field ID from 'fields.fieldId'
                if (!enabledFilters.has(fieldId)) {
                    // Clear the error for this disabled field
                    form.setFieldError(errorPath, "");
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledFilters]);
    // Initialize filters when modal opens
    useEffect(() => {
        if (opened && availableFilters.length > 0) {
            // If a default view is provided then we want to hydrate the form with the default view
            if (defaultView) {
                const defaultViewValues = convertPriorityViewToFormValues({
                    name: defaultView.name,
                    where: defaultView.filter?.where ?? {},
                    sort: (defaultView.filter?.sort ?? []).filter((item) => item != null)
                });
                const keys = Object.keys(defaultViewValues.fields);
                toggleFilter(keys);
                form.setValues(defaultViewValues);
            }
            setFilters(availableFilters);
        }
    }, [opened, availableFilters]);
    // Drag and drop sensors
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 5
        }
    }));
    const handleClose = () => {
        close();
        form.reset();
        setEnabledFilters(new Set());
        setPreviewFilters(null);
    };
    const toggleFilter = (filterId) => {
        const filterIds = Array.isArray(filterId) ? filterId : [filterId];
        const newEnabledFilters = new Set(enabledFilters);
        filterIds.forEach((id) => {
            if (newEnabledFilters.has(id)) {
                // Disable filter
                newEnabledFilters.delete(id);
                // Remove filter configuration
                const updatedFields = { ...form.values.fields };
                delete updatedFields[id];
                form.setFieldValue("fields", updatedFields);
            }
            else {
                // Enable filter
                newEnabledFilters.add(id);
                // Initialize filter configuration if it doesn't exist
                const filter = filters.find((f) => f.id === id);
                if (filter && !form.values.fields[id]) {
                    const defaultValues = getDefaultValuesForInputType(filter.inputType || "");
                    form.setFieldValue(`fields.${id}`, defaultValues);
                }
            }
        });
        setEnabledFilters(newEnabledFilters);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over)
            return;
        const source = active.data.current?.source;
        const filterId = active.data.current?.id;
        const overSource = over.data.current?.source ?? null;
        const overIdStr = typeof over.id === "string" ? over.id : "";
        // Only toggle when dropped over the OPPOSITE area
        if (source === "available") {
            // Must be dropped over enabled dropzone or an enabled item/container
            if (over.id === "enabled-filters-dropzone" ||
                overSource === "enabled" ||
                overIdStr.startsWith("enabled-")) {
                toggleFilter(filterId);
            }
        }
        else if (source === "enabled") {
            // Must be dropped over available dropzone or an available item/container
            if (over.id === "available-filters-dropzone" ||
                overSource === "available" ||
                overIdStr.startsWith("available-")) {
                toggleFilter(filterId);
            }
        }
    };
    // Drag UI state for highlighting droppable area
    const [activeDragSource, setActiveDragSource] = useState(null);
    const handleDragStart = (event) => {
        setActiveDragSource(event.active.data.current);
    };
    const resetDragState = () => {
        setActiveDragSource(null);
    };
    const sortOrderOptions = useMemo(() => {
        const options = [];
        // Add available sort options based on PrioritySearchSortKey enum
        // Currently only INQUIRED_AT is available
        const availableSortKeys = [
            { key: PrioritySearchSortKey.InquiredAt, label: "Inquired At" }
        ];
        availableSortKeys.forEach(({ key, label }) => {
            options.push({
                value: JSON.stringify({
                    field: key,
                    order: PrioritySearchSortOrder.Asc
                }),
                label: `${label} (Ascending)`
            });
            options.push({
                value: JSON.stringify({
                    field: key,
                    order: PrioritySearchSortOrder.Desc
                }),
                label: `${label} (Descending)`
            });
        });
        return options;
    }, []);
    const handlePreview = async () => {
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        const payload = convertFormToPriorityViewCreate(formValues);
        setPreviewFilters({
            ...DEFAULT_PREVIEW_FILTERS,
            where: payload.filter?.where ?? {},
            sort: payload.filter?.sort ?? []
        });
    };
    const submitPriorityView = async (event) => {
        event.preventDefault();
        const valid = form.validate();
        if (form.getValues().name === "") {
            form.setFieldError("name", "Name is required");
            return;
        }
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        const payload = convertFormToPriorityViewCreate(formValues);
        try {
            if (defaultView) {
                await updatePriorityView({
                    input: {
                        ...payload,
                        priorityViewId: defaultView.priorityViewId
                    }
                });
                showSuccessNotification(`${payload.name} filter was updated successfully!`);
            }
            else {
                await createPriorityView({ input: payload });
                showSuccessNotification(`${payload.name} filter was created successfully!`);
            }
            handleClose();
            refetchPriorityViews();
        }
        catch (error) {
            showErrorNotification();
        }
    };
    return (_jsxs(AppModal, { variant: "fullscreen", opened: opened, onClose: handleClose, fullScreen: true, withCloseButton: false, padding: 0, children: [_jsxs(Flex, { justify: "space-between", align: "center", p: "md", direction: { base: "column", md: "row" }, style: {
                    borderBottom: "1px solid var(--mantine-color-gray-2)",
                    boxShadow: "0 0 5px 0 color-mix(in srgb, var(--mantine-color-black) 10%, transparent)"
                }, children: [_jsx(Flex, { flex: 1, children: _jsx(Title, { order: 5, children: defaultView
                                ? "Edit Custom Filter"
                                : "Create Custom Filter" }) }), _jsxs(Flex, { gap: "xs", flex: 1.5, align: "center", my: { base: "xs", md: "0" }, children: [_jsx(TextInput, { placeholder: "Filter Name", value: form.values.name, flex: 1, w: { base: 300, lg: 350 }, ...form.getInputProps("name") }), _jsx(Button, { visibleFrom: "md", variant: "filled", loading: createPriorityViewFetching ||
                                    updatePriorityViewFetching, onClick: submitPriorityView, children: defaultView
                                    ? "Update Custom Filter"
                                    : "Save Custom Filter" })] }), _jsxs(Flex, { flex: 1, gap: "xs", justify: "flex-end", children: [Object.keys(form.values.fields).length > 0 && (_jsx(Button, { variant: "filled", onClick: handlePreview, children: "Preview" })), _jsx(Button, { variant: "outline", color: "gray", onClick: handleClose, disabled: createPriorityViewFetching ||
                                    updatePriorityViewFetching, children: "Cancel" }), _jsx(Button, { variant: "filled", hiddenFrom: "md", loading: createPriorityViewFetching ||
                                    updatePriorityViewFetching, onClick: submitPriorityView, children: defaultView ? "View" : "Save" })] })] }), _jsx(Flex, { h: { base: "100%", md: "100%" }, direction: { base: "column", md: "row" }, style: !isAtLeastMediumScreen ? { overflowY: "scroll" } : {}, children: _jsxs(DndContext, { sensors: sensors, collisionDetection: pointerWithin, onDragStart: handleDragStart, onDragEnd: (e) => {
                        handleDragEnd(e);
                        resetDragState();
                    }, onDragCancel: resetDragState, children: [_jsx(DraggableColumn, { gap: "xs", h: "100%", p: "md", w: { base: "auto", md: 220 }, miw: { base: "auto", md: 220 }, id: "available-filters-droppable", children: filters.map((filter) => (_jsx(AvailableFilterItem, { filter: filter, enabled: enabledFilters.has(filter.id), onToggle: toggleFilter, disabled: !isAtLeastMediumScreen }, filter.id))) }), _jsx(Divider, { orientation: "vertical" }), _jsxs(Stack, { gap: "xs", h: "100%", p: "md", pb: { base: 0, md: 100 }, w: { base: "auto  ", md: 300 }, miw: { base: "auto", md: 300 }, style: isAtLeastMediumScreen ? { overflowY: "scroll" } : {}, children: [_jsxs(Flex, { gap: "xs", align: "center", children: [_jsx(UnfoldMoreOutlined, { style: {
                                                fontSize: 18,
                                                color: "var(--mantine-color-gray-5)"
                                            } }), _jsx(Title, { order: 6, children: "Sorting" })] }), _jsx(Select, { placeholder: "Sort By", searchable: true, data: sortOrderOptions, w: "100%", mb: "md", size: "xs", ...form.getInputProps("sort"), value: form.values.sort.length > 0
                                        ? JSON.stringify(form.values.sort[0])
                                        : "", onChange: (value) => {
                                        if (value) {
                                            const sort = JSON.parse(value);
                                            form.setFieldValue("sort", [sort]);
                                        }
                                    } }), _jsxs(Flex, { gap: "xs", align: "center", children: [_jsx(FilterAltOutlined, { style: {
                                                fontSize: 16,
                                                color: "var(--mantine-color-gray-5)"
                                            } }), _jsx(Title, { order: 6, children: "Filtering" })] }), _jsx(DraggableColumn, { gap: "xs", flex: 1, id: "enabled-filters-droppable", children: Object.keys(form.values.fields).length > 0 ? (Object.keys(form.values.fields).map((id) => {
                                        const filter = filters.find((f) => f.id === id);
                                        if (!filter)
                                            return null;
                                        return (_jsx(FilterConfiguration, { filter: filter, removeFilter: toggleFilter, form: form, graphqlData: graphqlData, disabled: !isAtLeastMediumScreen }, filter.id));
                                    })) : (_jsx(Text, { variant: "meta-muted", children: "No filters enabled for filtering" })) })] }), _jsx(Divider, { orientation: "vertical" }), _jsx(Stack, { w: "100%", h: "100%", children: _jsx(Flex, { w: "100%", h: previewFilters ? "auto" : "100%", style: { overflow: "hidden" }, children: previewFilters ? (_jsx(PriorityViewTable, { priorityStatusList: priorityStatusList, isActiveTab: opened, readonly: true, containerStyle: {
                                        marginTop: 0,
                                        borderTop: "none"
                                    }, options: {
                                        searchBody: previewFilters ??
                                            {},
                                        setOpenModal: () => { },
                                        allowArchive: false // Disable archive functionality in preview
                                    }, modalHandlers: {
                                        priorityEntryDrawerOpen: () => {
                                            // No-op for readonly preview
                                            console.log("Preview mode: Priority entry drawer disabled");
                                        }
                                    } })) : (_jsx(PriorityViewPreviewEmptyState, { onPreviewPriorityView: Object.keys(form.values.fields)
                                        .length !== 0
                                        ? handlePreview
                                        : undefined })) }) }), _jsx(DragOverlay, { adjustScale: false, dropAnimation: null, children: activeDragSource
                                ? (() => {
                                    const draggingFilter = filters.find((f) => f.id === activeDragSource?.id);
                                    if (!draggingFilter)
                                        return null;
                                    const isFromAvailable = activeDragSource?.source === "available";
                                    if (isFromAvailable) {
                                        return (_jsx(Flex, { p: "md", w: { base: "auto", lg: 220 }, children: _jsx(AvailableFilterItem, { filter: draggingFilter, enabled: enabledFilters.has(draggingFilter.id), onToggle: toggleFilter, disabled: !isAtLeastMediumScreen, style: { width: "100%" } }, draggingFilter.id) }));
                                    }
                                    // From enabled: show the entire configuration card
                                    return (_jsx(Flex, { p: "md", w: { base: "auto", md: 300 }, children: _jsx(FilterConfiguration, { filter: draggingFilter, removeFilter: toggleFilter, form: form, graphqlData: graphqlData, disabled: !isAtLeastMediumScreen, style: { width: "100%" } }, draggingFilter.id) }));
                                })()
                                : null })] }) })] }));
};
